import { AddEditorDropdown } from "@app/components/members/AddEditorsDropdown";
import {
  useLiveDocMembers,
  useUpdateLiveDocMembers,
} from "@app/lib/swr/live_docs";
import { useUser } from "@app/lib/swr/user";
import type { LiveDocAccess } from "@app/types/live_docs";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Avatar,
  Button,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Plus,
  Spinner,
} from "@dust-tt/sparkle";

const accessLabels: Record<LiveDocAccess, string> = {
  read: "Can view",
  suggest: "Can suggest",
  write: "Can edit",
};

const accessOptions: LiveDocAccess[] = ["write", "suggest", "read"];

interface AccessMenuProps {
  access: LiveDocAccess;
  onChange: (access: LiveDocAccess) => void;
  onRemove?: () => void;
}

function AccessMenu({ access, onChange, onRemove }: AccessMenuProps) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          size="xs"
          variant="ghost"
          label={accessLabels[access]}
          isSelect
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup value={access}>
          {accessOptions.map((option) => (
            <DropdownMenuRadioItem
              key={option}
              value={option}
              label={accessLabels[option]}
              onClick={() => onChange(option)}
            />
          ))}
        </DropdownMenuRadioGroup>
        {onRemove && (
          <Button
            className="mt-1 w-full"
            size="xs"
            variant="ghost"
            label="Remove access"
            onClick={onRemove}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface LiveDocShareDialogProps {
  owner: LightWorkspaceType;
  docId: string;
  title: string;
  isOpen: boolean;
  onClose: () => void;
}

export function LiveDocShareDialog({
  owner,
  docId,
  title,
  isOpen,
  onClose,
}: LiveDocShareDialogProps) {
  const { members, isMembersLoading } = useLiveDocMembers({
    owner,
    docId,
    disabled: !isOpen,
  });
  const { share, unshare } = useUpdateLiveDocMembers({ owner, docId });
  // Only the owner opens this dialog, and the owner always has full access.
  const { user } = useUser();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Share “{title}”</DialogTitle>
        </DialogHeader>
        <DialogContainer>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground dark:text-muted-foreground-night">
              People in this doc's conversation can already edit it. Share it
              with others here; agents they ask never get more access than they
              have.
            </p>
            <AddEditorDropdown
              owner={owner}
              editors={[
                ...members.map((member) => member.user),
                ...(user ? [{ sId: user.sId }] : []),
              ]}
              onAddEditor={(member) => void share(member.sId, "write")}
              trigger={
                <Button
                  className="self-start"
                  size="sm"
                  variant="outline"
                  icon={Plus}
                  label="Add people"
                />
              }
            />
            {isMembersLoading ? (
              <Spinner size="sm" />
            ) : members.length === 0 ? (
              <p className="text-sm text-muted-foreground dark:text-muted-foreground-night">
                Not shared with anyone else yet.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {members.map(({ user, access }) => (
                  <li key={user.sId} className="flex items-center gap-3">
                    <Avatar
                      name={user.fullName}
                      visual={user.image ?? undefined}
                      size="sm"
                      isRounded
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium">
                        {user.fullName}
                      </span>
                      <span className="truncate text-xs text-muted-foreground dark:text-muted-foreground-night">
                        {user.email}
                      </span>
                    </div>
                    <AccessMenu
                      access={access}
                      onChange={(next) => void share(user.sId, next)}
                      onRemove={() => void unshare(user.sId)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContainer>
      </DialogContent>
    </Dialog>
  );
}
